/**
 * Shell portal target — the mount node for iPad-bound modals.
 *
 * The `.shell` is a fixed-pixel iPad-sized box (1180×820 landscape,
 * 820×1180 portrait) that CSS-scales to fit the viewport. Because it
 * carries a `transform: scale()`, it becomes the containing block for
 * any `position: fixed` DESCENDANT — so a modal rendered inside the
 * shell fixes to the iPad frame (not the browser viewport) and scales
 * with it. That gives a faithful iPad preview on desktop and guarantees
 * iPad fit on-device.
 *
 * App.tsx renders one `<div class="shell-portal">` inside `.shell` and
 * captures its ref here. Every iPad-bound modal does:
 *
 *     <Portal mount={shellPortal()}> … </Portal>
 *
 * instead of a bare `<Portal>` (which defaults to document.body and
 * escapes the frame). The mount node is available before any modal can
 * open (the shell mounts at app start), so `shellPortal()` is set by the
 * time a user opens a modal; if it were ever undefined, `<Portal>` falls
 * back to body — a graceful (if unscaled) degradation.
 *
 * DELIBERATELY body-portalled (NOT here): Matrix Viewer, Matrix Picker,
 * and the Timeline editor stay full-desktop-adaptable; rect-anchored
 * popovers (canvas right-click menu, logo menu, shape picker) position
 * at measured viewport coords and must stay in the viewport.
 */

import { createSignal } from 'solid-js';

const [shellNode, setShellPortal] = createSignal<HTMLElement | undefined>();
const [navigateNode, setNavigatePortal] = createSignal<HTMLElement | undefined>();

/** Where a modal mounts. While Navigate is open (2026-10-05: every section
 *  opens INSIDE it, app-study.html), modals mount in Navigate's own layer, so
 *  a popup a section opens - the pairing countdown, the QR scanner, a confirm,
 *  the Traffic Monitor - sits above Navigate instead of behind it. Otherwise
 *  the shell, as before. Navigate registers its node on mount and clears it on
 *  close, so nothing outlives it there. */
const shellPortal = (): HTMLElement | undefined => navigateNode() ?? shellNode();

export { shellPortal, setShellPortal, setNavigatePortal };
